import {
    memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
    type CSSProperties
} from 'react'
import { MessageSquare, PanelRightClose, Send } from 'lucide-react'
import { MriButton, MriInput, cn } from '@mriqbox/ui-kit'
import { MAX_CHAT_LENGTH, type ChatMessage, type Participant } from '@shared/protocol'
import { Avatar } from '@/components/Avatar'
import { linkify } from '@/lib/linkify'

const LARGURA_KEY = 'mri_chat_largura'
const LARGURA_PADRAO = 288
const LARGURA_MIN = 240
const LARGURA_MAX = 640
/** Passo do teclado: arrastar com as setas precisa ser util, nao milimetrico. */
const PASSO_TECLADO = 24

/**
 * Nunca deixa o painel comer a sala inteira: em tela estreita, metade da
 * largura ja e mais do que suficiente, e o resto precisa caber o palco.
 */
function limitar(largura: number): number {
    const teto = typeof window === 'undefined'
        ? LARGURA_MAX
        : Math.min(LARGURA_MAX, Math.max(LARGURA_MIN, window.innerWidth / 2))
    return Math.round(Math.min(teto, Math.max(LARGURA_MIN, largura)))
}

function lerLargura(): number {
    try {
        const salva = Number(localStorage.getItem(LARGURA_KEY))
        return Number.isFinite(salva) && salva > 0 ? limitar(salva) : LARGURA_PADRAO
    } catch {
        return LARGURA_PADRAO
    }
}

export interface ChatPanelProps {
    messages: ChatMessage[]
    /**
     * Quem esta na sala AGORA. A mensagem carrega a foto de quem a escreveu no
     * instante do envio; esta lista tem a foto atual, que e a que deve
     * aparecer.
     */
    participants: Participant[]
    selfId: string | null
    /**
     * Sub-sala cuja conversa esta na tela. Cada uma tem a sua, e sem o nome no
     * cabecalho nao da pra saber pra quem a mensagem vai.
     */
    channelName?: string
    onSend: (text: string) => void
    onCollapse: () => void
    className?: string
}

/**
 * Bate-papo da sala, na coluna da direita.
 *
 * Mensagens seguidas da mesma pessoa aparecem agrupadas, sem repetir avatar e
 * nome a cada linha: numa conversa rapida, repetir o cabecalho ocupa mais
 * espaco que o proprio texto.
 */
export function ChatPanel({
    messages,
    participants,
    selfId,
    channelName,
    onSend,
    onCollapse,
    className
}: ChatPanelProps) {
    const [draft, setDraft] = useState('')
    const [largura, setLargura] = useState(() => lerLargura())
    const [arrastando, setArrastando] = useState(false)
    /** Onde o arrasto comecou: x do ponteiro e largura naquele instante. */
    const inicio = useRef({ x: 0, largura: 0 })
    const fimRef = useRef<HTMLDivElement>(null)
    const listaRef = useRef<HTMLDivElement>(null)
    /**
     * So acompanha o fim quando a pessoa ja estava no fim. Quem subiu pra ler
     * algo nao pode ser arrastado de volta a cada mensagem nova.
     */
    const grudadoNoFim = useRef(true)

    useLayoutEffect(() => {
        if (grudadoNoFim.current) fimRef.current?.scrollIntoView({ block: 'end' })
    }, [messages])

    useEffect(() => {
        const lista = listaRef.current
        if (!lista) return
        const onScroll = () => {
            const sobra = lista.scrollHeight - lista.scrollTop - lista.clientHeight
            grudadoNoFim.current = sobra < 40
        }
        lista.addEventListener('scroll', onScroll)
        return () => lista.removeEventListener('scroll', onScroll)
    }, [])

    const gravarLargura = useCallback((valor: number) => {
        try {
            localStorage.setItem(LARGURA_KEY, String(valor))
        } catch {
            // Aba anonima ou storage bloqueado: vale so nesta sessao.
        }
    }, [])

    // A janela pode encolher depois de a largura ter sido escolhida numa tela
    // maior — sem isto o painel ficaria ocupando quase tudo.
    useEffect(() => {
        const onResize = () => setLargura((atual) => limitar(atual))
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        inicio.current = { x: event.clientX, largura }
        setArrastando(true)
    }

    const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!arrastando) return
        // O painel esta na direita: arrastar pra ESQUERDA aumenta.
        setLargura(limitar(inicio.current.largura - (event.clientX - inicio.current.x)))
    }

    const encerrarArrasto = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!arrastando) return
        event.currentTarget.releasePointerCapture(event.pointerId)
        setArrastando(false)
        gravarLargura(largura)
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const passo = event.key === 'ArrowLeft' ? PASSO_TECLADO
            : event.key === 'ArrowRight' ? -PASSO_TECLADO
                : 0
        if (passo === 0) return
        event.preventDefault()
        const proxima = limitar(largura + passo)
        setLargura(proxima)
        gravarLargura(proxima)
    }

    const enviar = (event: React.FormEvent) => {
        event.preventDefault()
        const texto = draft.trim()
        if (!texto) return
        onSend(texto)
        setDraft('')
        // Mandou: volta a acompanhar, mesmo que estivesse lendo mais acima.
        grudadoNoFim.current = true
    }

    useEffect(() => {
        if (!arrastando) return
        // Sem isto o arrasto vira selecao de texto da sala inteira.
        const anterior = document.body.style.userSelect
        document.body.style.userSelect = 'none'
        return () => { document.body.style.userSelect = anterior }
    }, [arrastando])

    return (
        <aside
            /*
             * A largura do arrasto vai como variavel de CSS, e nao como `width`
             * inline, porque ela so vale do `md` pra cima: no celular o painel
             * ocupa a tela inteira, e um `style` inline venceria a classe que
             * manda nisso. Dividir 390px entre video e bate-papo nao deixa nada
             * legivel dos dois lados.
             */
            style={{ '--chat-largura': `${largura}px` } as CSSProperties}
            className={cn(
                'relative flex shrink-0 flex-col border-l border-border/70',
                'w-full bg-card md:w-[var(--chat-largura)] md:bg-card/40',
                className
            )}
        >
            {/*
              * Alca de largura, colada na borda esquerda. Fina, mas com area de
              * clique folgada: uma barra de 1px e um alvo cruel de acertar. Com
              * `role="separator"` e as setas, quem nao usa mouse tambem ajusta.
              */}
            <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Ajustar a largura do bate-papo"
                aria-valuenow={largura}
                aria-valuemin={LARGURA_MIN}
                aria-valuemax={LARGURA_MAX}
                tabIndex={0}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={encerrarArrasto}
                onPointerCancel={encerrarArrasto}
                onKeyDown={onKeyDown}
                onDoubleClick={() => {
                    setLargura(LARGURA_PADRAO)
                    gravarLargura(LARGURA_PADRAO)
                }}
                title="Arraste para ajustar (duplo clique volta ao padrão)"
                className={cn(
                    // Sem alca no celular: la o painel e a tela inteira, nao
                    // uma coluna com largura pra ajustar.
                    'absolute inset-y-0 -left-1 z-10 hidden w-2 cursor-col-resize md:block',
                    'after:absolute after:inset-y-0 after:left-1 after:w-px after:transition-colors',
                    arrastando ? 'after:bg-primary' : 'after:bg-transparent hover:after:bg-primary/50',
                    'focus:outline-none focus-visible:after:bg-primary'
                )}
            />
            <div className="flex items-center justify-between border-b border-border/70 px-3 py-2">
                <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                    <span className="shrink-0">Bate-papo</span>
                    {channelName && (
                        <span className="truncate normal-case tracking-normal text-primary" title={channelName}>
                            · {channelName}
                        </span>
                    )}
                </span>
                <button
                    type="button"
                    onClick={onCollapse}
                    title="Encolher o bate-papo"
                    aria-label="Encolher o bate-papo"
                    className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <PanelRightClose className="h-4 w-4" />
                </button>
            </div>

            <div ref={listaRef} className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-2">
                {messages.length === 0 ? (
                    <p className="mt-6 px-2 text-center text-[11px] leading-relaxed text-muted-foreground">
                        Nada por aqui ainda.
                        <br />
                        O que for escrito some quando a sala acabar.
                    </p>
                ) : (
                    messages.map((message, indice) => {
                        const anterior = messages[indice - 1]
                        // Agrupa o que veio da mesma pessoa em sequencia e num
                        // intervalo curto: cabecalho a cada linha ocuparia mais
                        // espaco que a conversa.
                        const agrupada = !!anterior
                            && anterior.from === message.from
                            && message.at - anterior.at < 5 * 60_000

                        return (
                            <ChatLine
                                key={message.id}
                                message={message}
                                agrupada={agrupada}
                                isSelf={message.from === selfId}
                                // Foto de agora; a do envio so vale como
                                // reserva pra quem ja saiu da sala.
                                avatar={participants.find((p) => p.id === message.from)?.avatar
                                    ?? message.avatar}
                            />
                        )
                    })
                )}
                <div ref={fimRef} />
            </div>

            <form onSubmit={enviar} className="flex items-center gap-1.5 border-t border-border/70 p-2">
                <MriInput
                    value={draft}
                    maxLength={MAX_CHAT_LENGTH}
                    placeholder="Escreva algo..."
                    aria-label="Mensagem"
                    className="h-8 text-xs"
                    onChange={(event) => setDraft(event.target.value)}
                />
                <MriButton
                    type="submit"
                    size="sm"
                    className="h-8 w-8 shrink-0 p-0"
                    disabled={!draft.trim()}
                    title="Enviar"
                    aria-label="Enviar"
                >
                    <Send className="h-3.5 w-3.5" />
                </MriButton>
            </form>
        </aside>
    )
}

/**
 * Memoizada, e com o `linkify` memoizado por texto: arrastar a borda do painel
 * dispara `setLargura` a cada evento de ponteiro e re-renderiza a lista
 * inteira. Sem isto, cada frame do arrasto refazia o `matchAll` de todas as
 * mensagens do historico — o gesto que mais precisa ser fluido era o que mais
 * custava.
 */
const ChatLine = memo(function ChatLine({
    message,
    agrupada,
    isSelf,
    avatar
}: {
    message: ChatMessage
    agrupada: boolean
    isSelf: boolean
    avatar?: string
}) {
    const segmentos = useMemo(() => linkify(message.text), [message.text])

    return (
        <div className={cn('rounded-md px-2 py-0.5 hover:bg-accent/30', agrupada ? '' : 'mt-2 first:mt-0')}>
            {!agrupada && (
                <div className="mb-0.5 flex items-center gap-1.5">
                    <Avatar nickname={message.nickname} avatar={avatar} size={18} />
                    <span className={cn(
                        'truncate text-[11px] font-semibold',
                        isSelf ? 'text-primary' : 'text-foreground'
                    )}>
                        {message.nickname}
                    </span>
                    <span className="text-[9px] text-muted-foreground/70">{hora(message.at)}</span>
                </div>
            )}
            {/*
              * `break-words` e `whitespace-pre-wrap`: sem eles, uma palavra
              * gigante sem espaco estoura a largura da coluna e empurra o
              * layout inteiro.
              *
              * `select-text` porque a pagina inteira nasce com `user-select:
              * none` (styles.css). So o texto: o cabecalho fica de fora, e
              * arrastar por varias mensagens copia so o que foi escrito.
              */}
            <p className="select-text whitespace-pre-wrap break-words pl-[26px] text-xs leading-snug text-foreground/90">
                {segmentos.map((segment, indice) => (segment.kind === 'link' ? (
                    <a
                        // O indice serve de chave aqui: os pedacos nao tem id
                        // proprio e a linha inteira e remontada a cada render.
                        key={indice}
                        href={segment.href}
                        target="_blank"
                        /**
                         * `noopener` corta o `window.opener` da pagina aberta —
                         * sem ele, ela consegue trocar o endereco DESTA aba por
                         * uma copia da tela de entrada. `noreferrer` evita
                         * entregar de qual sala o clique veio.
                         */
                        rel="noopener noreferrer nofollow"
                        onClick={(event) => event.stopPropagation()}
                        className="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
                    >
                        {segment.value}
                    </a>
                ) : (
                    <span key={indice}>{segment.value}</span>
                )))}
            </p>
        </div>
    )
})

function hora(timestamp: number): string {
    return new Date(timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}
